import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Star, MapPin, ChevronDown } from "lucide-react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1628002881911-8bcdfbdf320e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxtZXRhbCUyMHJvb2YlMjBob3VzZXxlbnwwfHx8fDE3OTA5MzgxMDh8MA&ixlib=rb-4.1.0&q=85";

const LINES = ["QUALITY", "MATERIALS", "FOR YOUR", "BUILDING."];

const wrap = {
    hidden: {},
    show: { transition: { staggerChildren: 0.14, delayChildren: 0.3 } },
};
const line = {
    hidden: { y: "115%" },
    show: { y: "0%", transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1] } },
};

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

    return (
        <section ref={ref} id="top" className="relative min-h-[100svh] overflow-hidden flex items-end">
            <motion.div className="absolute inset-0" style={{ y, scale }}>
                <img
                    src={HERO_IMG}
                    alt="Standing seam metal roofline catching the sun"
                    className="w-full h-full object-cover img-treated"
                    data-testid="hero-backdrop-image"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F12] via-[#0B0F12]/60 to-[#0B0F12]/60" />
            <div className="absolute inset-0 corrugation opacity-60" />

            <p className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 rotate-90 origin-right font-mono2 text-[10px] uppercase tracking-[0.45em] text-white/40 whitespace-nowrap">
                Bono Region — Ghana
            </p>

            <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 pt-36 pb-14 sm:pb-20">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.15 }}
                    className="font-mono2 text-[10px] sm:text-xs uppercase tracking-[0.35em] text-amber-500 mb-5 flex items-center gap-3"
                >
                    <span className="inline-block w-10 h-px bg-amber-500/80" />
                    One God Roofing Limited Company
                </motion.p>

                <motion.h1
                    variants={wrap}
                    initial="hidden"
                    animate="show"
                    data-testid="hero-headline-reveal"
                    className="font-display font-extrabold uppercase leading-[0.93] text-[9.5vw] lg:text-[8rem] text-[#F8FAFC]"
                >
                    {LINES.map((text, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span variants={line} className="block">
                                {i === 3 ? (
                                    <>
                                        BUILDING<span className="text-amber-500">.</span>
                                    </>
                                ) : (
                                    text
                                )}
                            </motion.span>
                        </span>
                    ))}
                </motion.h1>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-8 flex flex-col lg:flex-row lg:items-end justify-between gap-8"
                >
                    <p data-testid="hero-subheading" className="max-w-md text-sm sm:text-base leading-relaxed text-[#94A3B8]">
                        Roof installation, inspection, repair and gutters for homes and
                        businesses — serving Sunyani and nearby areas.
                    </p>

                    <div className="flex flex-wrap items-center gap-3">
                        <span
                            data-testid="hero-rating-badge"
                            className="flex items-center gap-2.5 border border-white/15 bg-[#0B0F12]/70 backdrop-blur px-4 py-3"
                        >
                            <span className="flex text-amber-500">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} size={13} fill={i < 4 ? "currentColor" : "none"} strokeWidth={1.5} className={i === 4 ? "opacity-40" : ""} />
                                ))}
                            </span>
                            <span className="text-sm font-semibold text-white">4.4</span>
                            <span className="text-xs text-[#94A3B8]">7 Google reviews</span>
                        </span>
                        <span
                            data-testid="hero-location-badge"
                            className="flex items-center gap-2 border border-white/15 bg-[#0B0F12]/70 backdrop-blur px-4 py-3 text-xs text-[#94A3B8]"
                        >
                            <MapPin size={13} className="text-amber-500" />
                            J.H. Owusu Acheampong St, Sunyani
                        </span>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-8 flex flex-wrap gap-3"
                >
                    <a
                        href="#contact"
                        data-testid="hero-cta-quote-button"
                        className="group flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0F172A] font-semibold px-7 py-4 text-sm transition-colors"
                    >
                        Request a free quote
                        <ChevronDown size={16} className="transition-transform group-hover:translate-y-0.5" />
                    </a>
                    <a
                        href="tel:+233553237357"
                        data-testid="hero-cta-call-button"
                        className="flex items-center gap-2 border border-white/25 hover:border-amber-500 hover:text-amber-500 text-white px-7 py-4 text-sm transition-colors"
                    >
                        <Phone size={15} />
                        Call 055 323 7357
                    </a>
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
